<!--
  Component này được sử dụng để lựa chọn Grade, Class
  Danh sách class được lấy từ API, trong mỗi class trả về sẽ có các grade tương ứng
  Danh sách grade được tạo ra từ danh sách class
  Có thể bỏ lựa chọn từng phần
 -->
<template>
  <v-form ref="form">
    <v-row>
      <v-col :cols="gradeCol.cols" :sm="gradeCol.sm" :md="gradeCol.md">
        <v-autocomplete
          v-model="selectedGrade"
          :items="grades"
          label="Khối"
          item-text="title"
          item-value="id"
          no-data-text="Danh sách trống"
          clearable
          clear-icon="mdi-close"
          dense
          outlined
          hide-details="auto"
          return-object
          :rules="gradeRules"
          :loading="classLoading"
          @blur="onGradeBlur"
        ></v-autocomplete>
      </v-col>
      <v-col :cols="classCol.cols" :sm="classCol.sm" :md="classCol.md">
        <v-autocomplete
          v-model="selectedClass"
          :items="classes"
          label="Lớp"
          item-text="title"
          item-value="id"
          no-data-text="Danh sách trống"
          clearable
          clear-icon="mdi-close"
          dense
          outlined
          hide-details="auto"
          return-object
          :rules="classRules"
          :loading="classLoading"
          @change="onClassChange"
        ></v-autocomplete>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { get, isArray, isEmpty, orderBy } from 'lodash'
import { Class, Teaching } from '@/plugins/api'
import { mapGetters } from 'vuex'

// Get all grades by classes
const getGradeListFromClassList = (classList = []) => {
  if (!isArray(classList) || isEmpty(classList)) {
    return []
  }
  return [...new Set(classList.filter(c => c.grade).map(c => c.grade))]
}

export default {
  components: {},
  props: {
    defaultClass: {
      type: Object,
      default: () => {},
    },
    // Danh sách các class Id để fetch data
    // Dùng cho trường hợp login bằng tài khoản giáo viên
    // hoặc giới hạn số class được truy xuất dữ liệu
    searchClassIds: {
      type: Array,
      default: () => [],
    },

    gradeCol: {
      type: Object,
      default: () => ({
        cols: 12,
        sm: 12,
        md: 6,
      }),
    },
    classCol: {
      type: Object,
      default: () => ({
        cols: 12,
        sm: 12,
        md: 6,
      }),
    },
    gradeRules: {
      type: Array,
      default: () => [],
    },
    classRules: {
      type: Array,
      default: () => [],
    },
    autoSyncGradeByClass: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    selectedGrade: null,
    grades: [],
    selectedClass: null,
    classes: [], // Danh sách class để lựa chọn trên v-autocomplete
    allClasses: [], // Danh sách tất cả các class lấy về từ database
    selectedDay: null,
    days: [],
    classLoading: false,
    week: false,
  }),
  computed: {
    ...mapGetters('auth', ['ownClassIds', 'profile', 'role']),
    ...mapGetters('busDetail', ['weekdays']),
    dayList() {
      const days = orderBy(this.days, Object.keys(this.weekdays))
      return days
    },
  },
  async created() {
    await this.fetchAllClasses()
    this.classes = [...this.allClasses]
    this.grades = getGradeListFromClassList(this.allClasses)
  },
  methods: {
    // Get all classes from database
    async fetchAllClasses() {
      this.classLoading = true
      try {
        const params = { _limit: -1 }
        // Get classes filtered by role and head-teacher
        if (this.role.type === 'teacher') {
          if (this.$route.path.includes('marks-input')) {
            // lop giang day
            if (this.profile.id) {
              const teachingParams = {
                teacher: this.profile.id,
                _limit: -1,
              }
              const teachings = await Teaching.fetch(teachingParams)
              const teachingClassIds = [...new Set(teachings.map(t => t.class && t.class.id))]

              params['id_in'] = teachingClassIds
            }
          } else if (this.ownClassIds) {
            // lop chu nhiem
            params['id_in'] = this.ownClassIds
          }
        }
        if (!isEmpty(this.searchClassIds)) {
          params['id_in'] = [...new Set([...params['id_in'], ...this.searchClassIds])]
        }
        const response = await Class.getClassWithGrade(params)
        this.allClasses = isArray(response) && !isEmpty(response) ? response : []
      } catch (error) {
        console.error(error)
      }
      this.classLoading = false
    },

    onClassChange(newClass) {
      // Hủy lựa chọn lớp => Hủy lựa chọn khối và học sinh
      if (!get(newClass, 'id', '')) {
        this.selectedGrade = null
      }
    },

    onChange() {
      const data = {}
      data.grade = { ...this.selectedGrade }

      data.class = { ...this.selectedClass }
      this.$emit('changed', data)
    },
    /**
     * Xử lý tình huống khi xóa lựa chọn khối mà đang chọn lớp mà lost focus khối
     * thì chọn lại khối theo lớp đã chọn
     */
    onGradeBlur() {
      if (get(this.selectedClass, 'grade.id')) {
        this.selectedGrade = this.selectedClass.grade
      }
    },
    validate() {
      return this.$refs.form.validate()
    },
    reset() {
      this.selectedGrade = null
      this.selectedClass = null
      this.selectedDay = null
      this.week = false
    },
  },
  watch: {
    selectedGrade: {
      handler(newSelectedGrade) {
        if (get(newSelectedGrade, 'id')) {
          // Lựa chọn khối mới => filter danh sách class theo khối
          this.classes = this.allClasses.filter(c => get(c, 'grade.id') === newSelectedGrade.id)
          /**
           * Xóa lựa chọn lớp khi chọn khối khác
           * Xử lý tình huống chọn sang grade khác nhưng vẫn đang select class của grade cũ
           * VD :
           * Grade : Khối 6 => Select class Lớp 6A
           * Chuyển sang Grade Khối 7 => class 6A không nằm trong danh sách class của khối 7 nhưng selectedClass vẫn đang là 6A
           * Khi chuyển về khối 6 => auto select lớp 6A
           * => Giải quyết : Xóa selectedClass nếu class được chọn không nằm trong khối đang chọn
           */
          if (get(this.selectedClass, 'grade.id', '') !== newSelectedGrade.id) {
            this.selectedClass = null
          }
        } else {
          // Hủy lựa chọn khối => Reset danh sách lớp
          this.classes = [...this.allClasses]
        }
        this.onChange()
      },
    },
    selectedClass: {
      handler(newSelectedClass) {
        if (get(newSelectedClass, 'id') && this.autoSyncGradeByClass) {
          // Lựa chọn lớp => Auto chọn khối theo lớp
          this.selectedGrade = get(newSelectedClass, 'grade')
        }
        this.onChange()
      },
    },
    defaultClass: {
      handler(newClass) {
        if (get(newClass, 'id')) {
          this.selectedClass = newClass
        }
      },
    },
  },
}
</script>
